import { MouseEvent } from "react";
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    verticalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import SortableSelectCategory from "./TreeViewComponents/SortableSelectCategory";
import { createPortal } from "react-dom";
import { SelectCategory } from "./TreeViewComponents/SelectCategory";

import TreeView from "./TreeViewComponents/TreeView";
import { Skeleton } from "@mui/material";
import { useTreeViewApiRef } from "@mui/x-tree-view";
import SearchBar from "../../../components/Filtering/SearchBar";
import { ExtendedTreeViewBase, TreeSelectedCategory } from "../../../hooks/useModelFamilyTree";


export default function HierarchyTreeView({
    isLoading,
    isHierarchyDataLoading,
    selectedCategories,
    activeId,
    filteredTreeItems,
    expandedItems,
    selectedTreeItem,
    handleDragStart,
    handleDragEnd,
    handleSelectCategory,
    handleAddCategory,
    handleRemoveCategory,
    handleSelectItemFromTree,
    handleSearch,
    handleExpandedItemsChange,
    handleExpandAll,
    handleCollapseAll,
    allItemIds,
    tagCategories,
}: {
    isLoading: boolean,
    isHierarchyDataLoading: boolean,
    selectedCategories: TreeSelectedCategory[],
    activeId: string | null,
    filteredTreeItems: ExtendedTreeViewBase[],
    expandedItems: string[],
    selectedTreeItem: string | null,
    handleDragStart: (e: any) => void,
    handleDragEnd: (e: any) => void,
    handleSelectCategory: (e: any, name: string) => void,
    handleAddCategory: () => void,
    handleRemoveCategory: (id: string) => void,
    handleSelectItemFromTree: (e: React.SyntheticEvent<Element, Event> | null, id: string | null) => void,
    handleSearch: (searchKey: string, value: string) => void,
    handleExpandedItemsChange: (event: React.SyntheticEvent<Element, Event> | null, itemIds: string[]) => void,
    handleExpandAll: (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => void,
    handleCollapseAll: (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => void,
    allItemIds: string[],
    tagCategories: {
        value: string;
        label: string;
        color: string;
    }[]
}) {
    const apiRef = useTreeViewApiRef();
    
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    return (
        <div className='w-[400px] h-full p-6 flex flex-col justify-start items-stretch gap-8 '>
            <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
            >
                <div className="w-full flex flex-col justify-center items-start gap-3 text-default">
                    {isLoading ? (
                        <Skeleton variant='rounded' width='100%' height={44} />
                    ) : (
                    <>
                        <label className="text-[12px] font-medium">Organization Hierarchy</label>
                        <SortableContext
                            items={selectedCategories}
                            strategy={verticalListSortingStrategy}
                        >
                            {selectedCategories.map((selectedCategory, i) => (
                                <SortableSelectCategory 
                                    selectedCategory={selectedCategory}
                                    allCategories={[{ value: '', label: 'Select...', color: '' }].concat(tagCategories.filter(category => !selectedCategories.map(c => c.value).includes(category.value)))}
                                    index={i}
                                    handleSelectCategory={handleSelectCategory}
                                    handleAddCategory={handleAddCategory}
                                    handleRemoveCategory={handleRemoveCategory}
                                    count={selectedCategories.length}
                                />
                            ))}
                        </SortableContext>
                        {createPortal(<DragOverlay zIndex={9999}>
                            {activeId ? 
                                <SelectCategory 
                                    id={activeId} 
                                    selectedCategory={selectedCategories.find(category => category.id === activeId)!}
                                    index={selectedCategories.indexOf(selectedCategories.find(category => category.id === activeId)!)}
                                /> 
                                : null
                            }
                        </DragOverlay>, document.body)}
                    </>
                    )}
                </div>
            </DndContext>

            <div className="w-full grow overflow-hidden max-h-max flex flex-col justify-start items-stretch gap-4">
                {isLoading ? (
                    <Skeleton variant='rounded' width='100%' height={44} />
                ) : (
                    <div className="flex flex-col justify-start items-start gap-2">
                        <SearchBar 
                            searchFn={handleSearch}
                            label="Category"
                            searchKey="category"
                        />
                        <div className="flex justify-end items-center w-full">
                            <div className="pl-3 pr-[11px] border-r border-outline">
                                <button 
                                    disabled={expandedItems.length === allItemIds.length} 
                                    onClick={handleExpandAll} 
                                    className="text-[12px] disabled:text-disable disabled:font-normal text-primary font-medium"
                                >
                                    Expand All
                                </button>
                            </div>
                            <div className="px-3">
                                <button 
                                    disabled={expandedItems.length === 0} 
                                    onClick={handleCollapseAll} 
                                    className='text-[12px] disabled:text-disable disabled:font-normal text-primary font-medium'
                                >
                                    Collapse All
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="w-full grow max-h-max overflow-y-auto overflow-x-hidden scrollbox">
                    {isLoading || isHierarchyDataLoading ? (
                        <div className="w-full flex flex-col justify-start items-stretch gap-1">
                            {Array.from(Array(10).keys()).map(key => (
                                <Skeleton variant='rounded' width='100%' height={29}/>
                            ))}
                        </div>
                    ) : (
                        <TreeView 
                            treeItems={filteredTreeItems}
                            selectedItem={selectedTreeItem}
                            apiRef={apiRef}
                            expandedItems={expandedItems}
                            handleExpandedItemsChange={handleExpandedItemsChange}
                            handleSelectItemFromTree={handleSelectItemFromTree}
                        />
                    )}
                </div>
            </div>

            
        </div>
    )
}